// The heart's state is per product: colourways are separate products, and a
// card has no variant chosen to compare against. The variant only decides
// which `epi` a new save stores.
const CACHE_KEY = "wishlist-product-ids";

let swatPromise = null;
let loadPromise = null;
let items = [];
let productIds = new Set(readCache());

// Bumped by every add and remove. A fetch that started before one resolves
// with a list that predates it, and would undo the optimistic update.
let version = 0;

function readCache() {
  try {
    return JSON.parse(sessionStorage.getItem(CACHE_KEY)) || [];
  } catch {
    return [];
  }
}

function writeCache() {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify([...productIds]));
  } catch {
    // Storage unavailable: the hearts just fill a moment later.
  }
}

function notify() {
  writeCache();
  document.dispatchEvent(new CustomEvent("wishlist:change"));
}

/**
 * Resolves with Swym's SDK once it's ready. The Swym app embed loads it, in
 * SDK-only mode ("Show UI" unchecked on its Storefront UI Elements embed), so
 * Swym draws none of its own buttons or pages.
 *
 * @returns {Promise<object>} `window._swat`
 */
function swat() {
  swatPromise ??= new Promise((resolve) => {
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((sdk) => {
      const refreshEvent =
        sdk.JSEvents?.customerInfoRefreshed || "sw:customerInfoRefreshed";

      // A guest's list is merged into the account's on login.
      sdk.evtLayer?.addEventListener(refreshEvent, () => refresh());

      resolve(sdk);
    });
  });

  return swatPromise;
}

/**
 * Calls a callback-style SDK method as a Promise.
 *
 * @param {(sdk: object, resolve: Function, reject: Function) => void} fn
 */
function call(fn) {
  return swat().then(
    (sdk) => new Promise((resolve, reject) => fn(sdk, resolve, reject)),
  );
}

function refresh() {
  const startedAt = version;

  return call((sdk, resolve, reject) => sdk.fetch(resolve, reject)).then(
    (list) => {
      if (startedAt !== version) return;

      items = list || [];
      productIds = new Set(items.map(({ empi }) => String(empi)));
      notify();
    },
  );
}

/**
 * Fetches the list once per page. Later calls share the first request.
 */
function load() {
  loadPromise ??= refresh().catch((error) => {
    console.warn("[Wishlist] fetch failed", error);
  });

  return loadPromise;
}

/**
 * Runs a list change optimistically, then re-fetches the list either way:
 * to confirm it, or to roll the optimistic state back.
 *
 * @returns {Promise<boolean>} Whether Swym accepted the change. Resolves
 *   before the re-fetch, and never rejects, so callers can ignore it.
 */
function mutate(optimistic, request) {
  version += 1;
  optimistic();
  notify();

  const succeeded = request.then(
    () => true,
    (error) => {
      console.warn("[Wishlist] update failed", error);
      return false;
    },
  );

  succeeded
    .then(() => {
      // Also discards fetches that started while the request was in flight.
      version += 1;
      return refresh();
    })
    .catch((error) => console.warn("[Wishlist] fetch failed", error));

  return succeeded;
}

/**
 * @param {{ empi: number, epi: number, du: string }} product Product id,
 *   variant id and absolute product URL, plus any other Swym item fields
 */
function add(product) {
  return mutate(
    () => productIds.add(String(product.empi)),
    call((sdk, resolve, reject) => sdk.addToWishList(product, resolve, reject)),
  );
}

/**
 * Removes one saved item.
 *
 * @param {{ empi: number, epi: number, du: string }} item
 */
function removeItem({ empi, epi, du }) {
  return mutate(
    () => {
      items = items.filter(
        (saved) => !(saved.empi === empi && saved.epi === epi),
      );
      if (!items.some((saved) => saved.empi === empi)) {
        productIds.delete(String(empi));
      }
    },
    call((sdk, resolve, reject) =>
      sdk.removeFromWishList({ empi, epi, du }, resolve, reject),
    ),
  );
}

/**
 * Removes every saved variant of a product.
 *
 * @param {number|string} productId
 */
function remove(productId) {
  const saved = items.filter(({ empi }) => String(empi) === String(productId));

  return mutate(
    () => {
      items = items.filter(({ empi }) => String(empi) !== String(productId));
      productIds.delete(String(productId));
    },
    Promise.all(
      saved.map(({ empi, epi, du }) =>
        call((sdk, resolve, reject) =>
          sdk.removeFromWishList({ empi, epi, du }, resolve, reject),
        ),
      ),
    ),
  );
}

export const wishlist = {
  swat,
  load,
  add,
  remove,
  removeItem,
  has: (productId) => productIds.has(String(productId)),
  get items() {
    return items;
  },
};

// For page bundles, which would otherwise import a second copy with separate
// state.
window.wishlist = wishlist;

/**
 * The heart toggle on product cards and the PDP. Inside a `product-form` it
 * follows the selected variant, so a save stores the variant being viewed.
 */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("button");
    this.form = this.closest("product-form");

    this.button.addEventListener("click", this._onClick);
    this.form?.addEventListener("variant:changed", this._onVariantChanged);
    document.addEventListener("wishlist:change", this._render);

    this._render();
    wishlist.load();
  }

  disconnectedCallback() {
    this.button.removeEventListener("click", this._onClick);
    this.form?.removeEventListener("variant:changed", this._onVariantChanged);
    document.removeEventListener("wishlist:change", this._render);
  }

  get productId() {
    return this.getAttribute("product-id");
  }

  _onVariantChanged = (event) => {
    const { variant } = event.detail;
    if (variant) this.setAttribute("variant-id", variant.id);
  };

  _onClick = () => {
    if (wishlist.has(this.productId)) {
      wishlist.remove(this.productId);
      return;
    }

    // Only the ids and URL are required. The rest is what Swym's own emails
    // (shares, reminders) show for the item.
    wishlist.add({
      empi: Number(this.productId),
      epi: Number(this.getAttribute("variant-id")),
      du: this.getAttribute("product-url"),
      dt: this.getAttribute("product-title"),
      iu: this.getAttribute("image-url"),
      pr: Number(this.getAttribute("price")),
    });
  };

  _render = () => {
    const saved = wishlist.has(this.productId);

    this.toggleAttribute("saved", saved);
    this.button.setAttribute(
      "aria-label",
      saved ? this.dataset.labelRemove : this.dataset.labelAdd,
    );
  };
}

if (!customElements.get("wishlist-button")) {
  customElements.define("wishlist-button", WishlistButton);
}
